import { z } from "zod";
import type { BisonEnvelope } from "./bison-contracts.ts";
import { campaignActionSchema } from "./campaign-config.ts";
import { triggerOwnWords } from "./lead-scheduling-link.ts";
import { type MessageRegionsRecord, ownWordsFromRegions } from "./message-regions.ts";
import {
  type ClassificationDecision,
  ClassificationDecisionSchema,
  type ReplyStanceSchema,
} from "./reply-classification.ts";

/**
 * A no with a named condition (`docs/2026-10-03-conditional-no.md`). Dawid, about 01:35 CEST on
 * 2026-10-03: "Rep decides: show it, no draft". A decline the host would close today stops instead
 * with a card asking the rep whether the lead's condition holds; Yes redrafts the turn as an answer
 * that says so, No leaves it with the other noes. A host rule on the classifier's labels and the
 * lead's own words: no model, no new label.
 *
 * Pure and workflow-safe: the drafting pipeline runs it inside a Temporal workflow.
 */

/** The patch pair; the worker answers it per Bison workspace (`classifier-split-activation.ts`). */
export const CONDITIONAL_NO_PATCH = "conditional-no-v1";
export const CONDITIONAL_NO_OFF_PATCH = "conditional-no-off-v1";

/** The run action a card turn ends with; `run-projection.ts` turns it into the rep's step. */
export const CONDITIONAL_NO_ACTION = "confirm_condition";

/**
 * The rep's step is a `manual` step whose id is `conditional-no-condition-r<conversation revision>-
 * c<config revision>` (`run-projection.ts` `stepId`); the id is what tells it from any other chore.
 */
export const CONDITIONAL_NO_STEP_PREFIX = "conditional-no";
export const CONDITIONAL_NO_STEP_KEY = "condition";

/** The host's evidence row on a lifted turn: the rep's confirmation, for this reply only. */
export const CONDITIONAL_NO_EVIDENCE_ID = "turn:confirmed_condition";

/** The stances the host closes as a decline today, and the only ones this rule reads. */
export const CONDITIONAL_NO_STANCES: ReadonlySet<z.infer<typeof ReplyStanceSchema>> = new Set([
  "not_interested_soft",
  "not_interested_hard",
  "timing_defer",
]);

/**
 * What a run records, beside the run (`run-details/<hash>.conditional-no.json`). `applied` is a card
 * turn: closed for the rep. `lifted` is the rep's Yes: the turn drafted as an answer.
 */
export const ConditionalNoRecordSchema = z.strictObject({
  version: z.literal(1),
  state: z.enum(["applied", "lifted"]),
  sentence: z.string().min(1),
  connector: z.string().min(1),
  evidence_id: z.string().min(1),
});
export type ConditionalNoRecord = z.infer<typeof ConditionalNoRecordSchema>;
export type ConditionSentence = { sentence: string; connector: string };
export type ConditionalNoFinding = ConditionSentence & { evidence_id: string };

/**
 * The connectors, whole words, any case. A bare "not until" names a time ("not until January"), a
 * deferral rather than a condition the seller can meet, so it counts only before a subject.
 */
const CONNECTOR =
  /\b(unless|only\s+if|if\s+your|if\s+you|as\s+long\s+as|provided\s+that|once\s+you|not\s+until(?=\s+(?:you|your|they|we|there|it)\b))\b/iu;

/** An embedded question ("see if you could") or a removal request is not a condition. */
const NOT_A_CONDITION = [
  /\b(?:see|wonder|wondering|ask|asking|check|checking|know)\s+if\s+you\b/iu,
  /\bif\s+you\s+(?:could|can|would|will)\s+(?:please\s+)?(?:remove|unsubscribe|stop|take\s+(?:me|us)\s+off)\b/iu,
  /\bif\s+you\s+(?:don['’]?t|do\s+not)\s+mind\b/iu,
];

/**
 * Legal and clinical boilerplate the signature cut misses, under a bare "No". Fitted to the
 * evaluation pools (the design's "Measured on the pools"): a disclaimer worded otherwise can still
 * read as a condition, which costs the rep one click. Read only on the signature cut's words: where
 * the classification call labelled the trigger's lines (`message-regions.ts`), a disclaimer is
 * already outside the lead's own words and this list is not consulted.
 */
const BOILERPLATE =
  /\bintended\s+recipient\b|\bin\s+error\b|\bconfidential\b|\bprivileged\b|\bmental\s+health\b|\bcrisis\b|\bemergency\b|\b911\b|\bclick\s+(?:on\s+)?links\b|\bunsubscribe\b|\bdisclos(?:e|ure)\b|\belectronic\s+communications?\b|\bnot\s+secure\b|\bsession\s+fee\b|\b(?:cancel|change|reschedule)\b[^.]{0,60}\b(?:appointment|session|visit)\b/iu;

/** Sentences at `.`, `!` or `?` before a space, and at blank lines; runs of whitespace become one space. */
function sentencesOf(words: string): string[] {
  return words
    .split(/\n\s*\n/u)
    .flatMap((paragraph) =>
      paragraph
        .replace(/\s+/gu, " ")
        .trim()
        .split(/(?<=[.!?])\s+/u),
    )
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

/**
 * The first sentence of the lead's own words that names a condition the seller could meet, or null.
 * `labelled` says the words are the message regions the classification call labelled, which leave
 * disclaimers out, so the boilerplate list fitted to the signature cut is skipped.
 */
export function conditionSentence(
  words: string | null,
  labelled = false,
): ConditionSentence | null {
  if (words === null) return null;
  for (const sentence of sentencesOf(words)) {
    const match = CONNECTOR.exec(sentence);
    if (!match?.[1]) continue;
    if (NOT_A_CONDITION.some((pattern) => pattern.test(sentence))) continue;
    if (!labelled && BOILERPLATE.test(sentence)) continue;
    return { sentence, connector: match[1].toLowerCase().replace(/\s+/gu, " ") };
  }
  return null;
}

/**
 * The trigger's condition sentence and its evidence id. Read from the message regions where the
 * classification call labelled the trigger's lines; from nothing where those labels failed the
 * host's check; and from the words above the signature cut only on a run that was never asked for
 * them, which is every run of a workspace whose message-regions switch is off.
 */
export function conditionalNoFinding(
  input: BisonEnvelope,
  regions?: MessageRegionsRecord,
): ConditionalNoFinding | null {
  const trigger = input.messages.find(
    (message) => message.provider_id === input.trigger.id && message.direction === "inbound",
  );
  if (!trigger) return null;
  const labelled = ownWordsFromRegions(regions);
  const found =
    labelled === undefined
      ? conditionSentence(triggerOwnWords(input))
      : conditionSentence(labelled, true);
  return found ? { ...found, evidence_id: trigger.id } : null;
}

/** A decline the host would close today, on a stance this rule reads. */
export function conditionalNoEligible(
  stance: z.infer<typeof ReplyStanceSchema>,
  decision: Pick<ClassificationDecision, "disposition" | "turn_treatment">,
): boolean {
  return (
    decision.disposition === "final_refusal" &&
    decision.turn_treatment === "close_decline" &&
    CONDITIONAL_NO_STANCES.has(stance)
  );
}

/** What `answer_then_close` allows, as `classificationDecision` has it for a no that asks something. */
const ANSWER_THEN_CLOSE_STEPS: ReadonlySet<string> = new Set([
  "acknowledge",
  "answer",
  "share_resource",
  "manage_booking",
]);

/**
 * The rep's Yes for one turn: the decline becomes an answer that is then closed, the treatment the
 * classifier already gets for a no that carries a question. Historical decisions keep calls
 * blocked. With the confirmed-condition call patch, only the two meeting actions also become
 * available; campaign permission is still checked and handoff stays blocked.
 */
export function liftedConditionalNoDecision(offerCall = false): ClassificationDecision {
  return ClassificationDecisionSchema.parse({
    disposition: "answer",
    turn_treatment: offerCall ? "normal" : "answer_then_close",
    conditional_yes: offerCall,
    blocked_next_steps: campaignActionSchema.options.filter(
      (action) =>
        !ANSWER_THEN_CLOSE_STEPS.has(action) &&
        !(offerCall && (action === "share_booking_link" || action === "offer_meeting")),
    ),
  });
}

/** The description the run's action carries: what an older build shows as the step's text. */
export function conditionalNoActionDescription(): string {
  return "A no with a condition: they said no unless something holds. Check whether it holds for this campaign; nothing was drafted.";
}

/** The host's evidence row on a lifted turn, beside `campaign:handoff` in the stages' evidence. */
export function conditionalNoEvidence(record: ConditionalNoRecord, simulation: boolean) {
  return {
    id: CONDITIONAL_NO_EVIDENCE_ID,
    text: `The rep confirmed for this reply that the lead's condition holds: ${JSON.stringify(record.sentence)}`,
    authority: simulation ? ("simulation_only" as const) : ("rep_confirmed" as const),
  };
}

/** Whether a plan step is the card's step. */
export function isConditionalNoStep(step: { id: string; input: { kind: string } }): boolean {
  return (
    step.input.kind === "manual" && /^conditional-no-condition-r[0-9]+-c[0-9]+$/u.test(step.id)
  );
}
